import { useEffect, useMemo, useState, useRef, useCallback, type RefObject } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Grid,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'

import { Save, Send, Plus, Pencil, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccess } from '@/hooks/useAccess'
import { useStickyPanelMaxHeight } from '@/hooks/useStickyPanelMaxHeight'
import { useAuthStore } from '@/store/auth'
import { canReassignParentTicketAgent } from '@/lib/utils/roleHelper'
import { ClientService, type BillingClientOption } from '@/lib/services/client'
import { ClientBillingSelection } from '@/components/forms/parentTicket/ClientBillingSelection'
import { mapApiRequestToRequestOption, TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useParentTicketDraftStore } from '@/store/parentTicketDraft'
import { useParentTicketStore, type ParentTicketWithMeta } from '@/store/parentTicket'
import type {
  AgentOption,
  ClientOption,
  IncidentLocation,
  ClientPurchaseOrder,
  ParentTicketFormValues,
  TicketPartFormRow,
  ParentTicketAttachmentRow,
  RequestOption,
} from '@/types/ticket'
import { CreateTicketRequestModal } from '@/components/forms/CreateTicketRequestModal'
import { FormPanel, MockupFieldLabel } from '@/components/forms/parentTicket/FormPanel'
import RequestDetailsCollapse from '@/components/forms/parentTicket/RequestDetailsCollapse'
import { ParentTicketCategoryCascade } from '@/components/forms/parentTicket/ParentTicketCategoryCascade'
import { TeamSearchSelect } from '@/components/forms/parentTicket/TeamSearchSelect'
import { ToolPartNameInput } from '@/components/forms/parentTicket/ToolPartNameInput'
import { ParentTicketAttachmentSection } from '@/components/forms/parentTicket/ParentTicketAttachmentSection'
import FEDispatchFlow, {
  type FEDispatchSelection,
  shouldHydrateFormFromTicket,
} from '@/components/forms/parentTicket/FEDispatchFlow'
import { dispatchSelectionSnapshot } from '@/components/forms/parentTicket/feDispatchPersistence'
import { resolveDispatchScheduleIds } from '@/components/forms/parentTicket/feDispatchScheduleIds'
import { buildDispatchSelectionsFromTicket } from '@/components/forms/parentTicket/feDispatchSelectionFromTicket'
import { isTerminalParentStatus } from '@/components/forms/parentTicket/feDispatchUtils'
import { resolveDispatchSelectionsOnToggle } from '@/components/forms/parentTicket/dispatchToggleState'
import {
  clearIncidentLocationGeoFields,
  incidentLocationFieldsFromNominatim,
  storeFieldsFromClientOption,
} from '@/components/forms/parentTicket/feDispatchSelectionUtils'
import { resolveSeedLocationAddress } from '@/components/forms/parentTicket/seedDispatchLocation'
import type { FreeTextLocationResult } from '@/types/location'
import { ParentTicketFormContextRail } from '@/components/forms/parentTicket/ParentTicketFormContextRail'
import TaskBadge from '@/components/helpdesk/TicketDetail/tasks/TaskBadge'
import TaskDetailDrawer from '@/components/helpdesk/TicketDetail/tasks/TaskDetailDrawer'
import {
  countTasksBySourceField,
  defaultCreatePayloadForSourceField,
  formatSourceFieldCounter,
  type TaskSourceField,
} from '@/components/helpdesk/TicketDetail/tasks/taskSourceFieldUtils'
import { childTicketService, type ChildTicketTask } from '@/lib/services/childTicketService'
import { apiClient } from '@/lib/api/client'
import { useTicketOptions } from '@/hooks/useTicketOptions'
import { mergeSupportTypeSelectOptions, useSupportTypeOptions } from '@/hooks/useSupportTypeOptions'
import { mergeTicketSelectOptions } from '@/lib/services/ticketOptionListService'
import { geographicService, type GeographicOption } from '@/lib/services/geographicService'
import { ticketToFormValues, buildMetadataPayload, buildEffectiveFormForSubmit, clearedDispatchContextFields } from '@/lib/utils/parentTicketFormPayload'
import {
  getDuplicatePartIndexes,
  isReadyDispatchSubmitAllowed,
  validateParentTicketSubmit,
} from '@/lib/utils/parentTicketSubmitValidation'
import { hasFiniteCoords } from '@/components/forms/parentTicket/dispatchSitePin'
import { formatWallClockDateTime } from '@/lib/utils/dateTimeUtils'
import {
  flattenValidationErrors,
  getDispatchFieldErrors,
  hasDispatchFieldErrors,
} from '@/lib/utils/formValidationErrors'
import { buildTicketRequestSubject } from '@/lib/utils/ticketRequestSubject'
import {
  deriveTierFromServiceCode,
  resolveServiceMeta,
} from '@/lib/utils/serviceTypeMapper'

export type ParentTicketFormProps = {
  /** When set, form is in edit mode: load ticket and update on submit */
  parentTicketId?: string
  onSuccess?: (ticketId: string) => void
  onCancel?: () => void
}

/**
 * Form field scope (task-driven workflow):
 * Create + Edit: request, PO, subject, severity, description, agent, notes.
 * Store → FE Plotting task. Assets → CSO Review task. FE/parts/permit → Tasks tab.
 */

function agentLabel(a: AgentOption) {
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
  return name || a.user_login || `User #${a.id}`
}

function fmtDatetimeLocal(iso: string) {
  return formatWallClockDateTime(iso)
}

function formatPoDate(value?: string | null) {
  if (!value) return '-'
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString()
  } catch {
    return value
  }
}

function requestLabel(r: RequestOption) {
  const bits = [
    r.id,
    r.contactName || r.clientName,
    r.clientOrg,
  ].filter(Boolean)
  return bits.join(' | ')
}

function normalizeTicketSource(source?: string | null): string {
  if (!source) return ''
  const normalized = source.trim().toLowerCase()
  if (normalized === 'web') return 'client_portal'
  if (normalized === 'manual') return 'atlas_app'
  return normalized
}

/** Generate a unique ticket ID for new tickets (agent can share before submit). */
function generateTicketId(): string {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const min = String(now.getMinutes()).padStart(2, '0')
  const ss = String(now.getSeconds()).padStart(2, '0')
  const dateTime = `${yyyy}${mm}${dd}${hh}${min}${ss}`
  const random = Math.random().toString(36).slice(2, 8).toUpperCase()
  return `PT-${dateTime}-${random}`
}


function createInitialFormState(): ParentTicketFormValues {
  return {
    requestId: '',
    storeId: undefined,
    storeCode: '',
    incidentLocationType: '',
    incidentLocationName: '',
    incidentLocationAddress: '',
    incidentLocationLatitude: null,
    incidentLocationLongitude: null,
    incidentLocationPlaceId: null,
    incidentLocationSource: null,
    incidentLocationMunicipality: null,
    incidentLocationProvince: null,
    incidentLocationCountry: null,
    ticketId: '',
    subject: '',
    priority: '3',
    severity: '3',
    supportType: '',
    serviceTypeId: undefined,
    source: '',
    clientReferenceNumber: '',
    billable: null,
    assignedTeamId: undefined,
    resolverTeamId: undefined,
    issueCategoryId: undefined,
    issueSubCategoryId: undefined,
    issueItemId: undefined,
    regionId: undefined,
    areaClusterId: undefined,
    provinceId: undefined,
    townId: undefined,
    issueDescription: '',
    agentId: '',
    notes: '',
    hasPartsNeeded: false,
    parts: [],
    hasAttachmentsNeeded: false,
    attachmentRows: [],
    certificationRequired: false,
    brandId: null,
  }
}

export function ParentTicketForm({
  parentTicketId,
  onSuccess,
  onCancel,
}: ParentTicketFormProps = {}) {
  const colors = useThemeColors()
  const access = useAccess().hasAccess('/tickets')
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const { options: severityOptions, labelMap: severityLabelMap, loading: severityLoading } = useTicketOptions('severity')
  const { options: sourceOptions, labelMap: sourceLabelMap, loading: sourceLoading } = useTicketOptions('ticket_source')
  const { options: billingTypeOptions, loading: billingTypeLoading } = useTicketOptions('billing_type')
  const { options: onsiteHoldReasonOptions } = useTicketOptions('onsite_hold_reason')
  const { options: onsiteSupportDurationOptions } = useTicketOptions('onsite_support_duration')
  const { options: supportTypeOptions, labelByCode: supportTypeLabelMap, loading: supportTypeLoading } = useSupportTypeOptions()
  const currentUser = useAuthStore((s) => s.user)
  const canAssignAgent = canReassignParentTicketAgent(currentUser)
  const getTicketById = useParentTicketStore((s) => s.getTicketById)
  const mergeTicket = useParentTicketStore((s) => s.mergeTicket)
  const editTicket = useParentTicketStore((s) =>
    parentTicketId ? s.tickets.find((t) => String(t.id) === String(parentTicketId)) : undefined,
  )
  const dispatchHydratedRef = useRef(false)
  const formHydratedTicketIdRef = useRef<string | null>(null)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingMode, setSavingMode] = useState<'draft' | 'submit' | null>(null)
  const isEditMode = !!parentTicketId

  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const dispatchFieldErrors = useMemo(() => getDispatchFieldErrors(errors), [errors])

  useEffect(() => {
    if (dispatchFieldErrors.length > 0) {
      dispatchPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [dispatchFieldErrors])

  // Request search states
  const [requestSearchTerm, setRequestSearchTerm] = useState('')
  const [requestSearchResults, setRequestSearchResults] = useState<RequestOption[]>([])
  const [isRequestSearching, setIsRequestSearching] = useState(false)
  const [selectedRequestOption, setSelectedRequestOption] = useState<RequestOption | null>(null)
  const [pendingClient, setPendingClient] = useState<{ requestId: string; client: BillingClientOption } | null>(null)
  const selectedBillingClient = !selectedRequestOption?.clientId && pendingClient?.requestId === selectedRequestOption?.id
    ? pendingClient?.client ?? null : null
  const effectiveClientId = selectedRequestOption?.clientId || selectedBillingClient?.id
  const effectiveClientCode = selectedRequestOption?.clientCode || selectedBillingClient?.code
  const effectiveClientName = selectedRequestOption?.clientId
    ? selectedRequestOption.clientLabel || selectedRequestOption.clientOrg || selectedRequestOption.clientName || `Client #${selectedRequestOption.clientId}`
    : selectedBillingClient?.name
  const requestSearchInputRef = useRef<HTMLInputElement>(null)
  const requestSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const selectedRequestDetailsRef = useRef<HTMLDivElement>(null)
  const dispatchPanelRef = useRef<HTMLDivElement>(null)
  const railStickyRef = useRef<HTMLDivElement>(null)
  const railScrollRef = useRef<HTMLDivElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)
  const severityRef = useRef<HTMLSelectElement>(null)
  const issueDescriptionRef = useRef<HTMLTextAreaElement>(null)
  const railMaxHeight = useStickyPanelMaxHeight(railStickyRef)

  // Agent search states (Helpdesk users)
  const [agentSearchTerm, setAgentSearchTerm] = useState('')
  const [agentSearchResults, setAgentSearchResults] = useState<AgentOption[]>([])
  const [isAgentSearching, setIsAgentSearching] = useState(false)
  const [selectedAgentOption, setSelectedAgentOption] = useState<AgentOption | null>(null)
  const [assignToMe, setAssignToMe] = useState(false)
  const agentSearchInputRef = useRef<HTMLInputElement>(null)
  const agentSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const creatorAgentSeededRef = useRef(false)

  // Client purchase order states
  const [purchaseOrders, setPurchaseOrders] = useState<ClientPurchaseOrder[]>([])
  const [isLoadingPurchaseOrders, setIsLoadingPurchaseOrders] = useState(false)
  const [purchaseOrderLoadError, setPurchaseOrderLoadError] = useState<string | null>(null)
  const [purchaseOrderRefresh, setPurchaseOrderRefresh] = useState(0)
  const loadedPurchaseOrdersClientIdRef = useRef<number | null>(null)

  const [prefilledFields, setPrefilledFields] = useState<Record<string, boolean>>({})

  const [serviceParents, setServiceParents] = useState<Array<{ id: number; name: string; code?: string }>>([])
  const [serviceChildren, setServiceChildren] = useState<
    Array<{ id: number; name: string; code?: string; parent_id: number }>
  >([])
  const [serviceParentId, setServiceParentId] = useState('')
  const [serviceTypesLoading, setServiceTypesLoading] = useState(false)
  const [regionOptions, setRegionOptions] = useState<GeographicOption[]>([])
  const [areaClusterOptions, setAreaClusterOptions] = useState<GeographicOption[]>([])
  const [provinceOptions, setProvinceOptions] = useState<GeographicOption[]>([])
  const [townOptions, setTownOptions] = useState<GeographicOption[]>([])
  const [geographicLoading, setGeographicLoading] = useState(false)
  const [provinceLoading, setProvinceLoading] = useState(false)
  const [townLoading, setTownLoading] = useState(false)

  const { isOpen: isAddRequestModalOpen, onOpen: onAddRequestModalOpen, onClose: onAddRequestModalClose } = useDisclosure()
  const { isOpen: confirmUncheckOpen, onOpen: openConfirmUncheck, onClose: closeConfirmUncheck } = useDisclosure()
  const {
    isOpen: taskDrawerOpen,
    onOpen: openTaskDrawer,
    onClose: closeTaskDrawer,
  } = useDisclosure()
  const [linkedPartsForConfirm, setLinkedPartsForConfirm] = useState<
    Array<{ part_name: string; child_ticket_id?: number | null; taskNumber?: string | null }>
  >([])

  const [form, setForm] = useState<ParentTicketFormValues>(createInitialFormState)
  const [ticketTasks, setTicketTasks] = useState<ChildTicketTask[]>([])
  const coordinationTask = useMemo(
    () => ticketTasks.find((task) => task.action_type === 'onsite_coordination') ?? null,
    [ticketTasks],
  )
  const [editingTask, setEditingTask] = useState<ChildTicketTask | null>(null)
  const [taskCreateDefaults, setTaskCreateDefaults] = useState<Partial<ChildTicketTask> | null>(null)
  const [requiresDispatch, setRequiresDispatch] = useState(false)
  const [dispatchReadiness, setDispatchReadiness] = useState<'pending' | 'on_hold' | 'ready'>('pending')
  const [dispatchHoldReason, setDispatchHoldReason] = useState('')
  const [dispatchHoldRemarks, setDispatchHoldRemarks] = useState('')
  const [dispatchExpectedFollowUp, setDispatchExpectedFollowUp] = useState('')
  const [dispatchChangeReason, setDispatchChangeReason] = useState('Parent-ticket requirements or engineer selection changed')
  const [dispatchWindow, setDispatchWindow] = useState<{ from: string; to: string; durationMinutes?: number; manualOverride?: boolean; overrideReason?: string } | undefined>()
  const isSubmittedTicket = Boolean(editTicket?.ticketSubmittedAt)
  const [dispatchSelection, setDispatchSelection] = useState<FEDispatchSelection | null>(null)
  const [dispatchAdditionalSelections, setDispatchAdditionalSelections] = useState<FEDispatchSelection[]>([])
  /** Sync mirror so backup persist can see primary before React re-renders. */
  const dispatchSelectionRef = useRef<FEDispatchSelection | null>(null)
  /** Last successfully persisted dispatch snapshot (edit mode) — skip redundant Save persist. */
  const lastPersistedDispatchSnapshotRef = useRef('')
  /** User unchecked "Requires field engineer dispatch" — don't auto re-enable from ticket meta. */
  const dispatchOptOutRef = useRef(false)
  const [dispatchWarning, setDispatchWarning] = useState<string | null>(null)
  const [hasUnresolvedInvalidDispatch, setHasUnresolvedInvalidDispatch] = useState(false)
  const [sitePinConfirmed, setSitePinConfirmed] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)

  const {
    requestNavigation,
  } = useUnsavedChangesGuard({ isDirty: isFormDirty, message: 'Changes might not be saved' })

  const markFormDirty = useCallback(() => setIsFormDirty(true), [])
  const severitySelectOptions = useMemo(
    () => mergeTicketSelectOptions(
      severityOptions,
      String(form.severity ?? form.priority ?? ''),
      severityLabelMap,
    ),
    [severityOptions, form.severity, form.priority, severityLabelMap],
  )
  const severitySelectValue = String(form.severity ?? form.priority ?? '')
  const sourceSelectOptions = useMemo(
    () => mergeTicketSelectOptions(sourceOptions, form.source, sourceLabelMap),
    [sourceOptions, form.source, sourceLabelMap],
  )
  const supportTypeSelectOptions = useMemo(
    () => mergeSupportTypeSelectOptions(
      supportTypeOptions,
      form.supportType,
      supportTypeLabelMap,
    ),
    [supportTypeOptions, form.supportType, supportTypeLabelMap],
  )
  const billableSelectValue =
    form.billable === null || form.billable === undefined
      ? ''
      : form.billable
        ? 'true'
        : 'false'
  const serviceTypesClientCode = effectiveClientCode?.trim() || undefined
  const filteredServiceChildren = useMemo(() => {
    if (!serviceParentId) return []
    return serviceChildren.filter((st) => String(st.parent_id) === serviceParentId)
  }, [serviceParentId, serviceChildren])
  const hasChildServiceTypes = filteredServiceChildren.length > 0
  const draft = useParentTicketDraftStore((s) => s.draft)
  const hydratedFromRequestId = useParentTicketDraftStore(
    (s) => s.hydratedFromRequestId,
  )
  const [appliedDraftRequestId, setAppliedDraftRequestId] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    if (!hydratedFromRequestId) return
    if (appliedDraftRequestId === hydratedFromRequestId) return

    setForm((prev) => ({
      ...prev,
      requestId: draft.requestId || prev.requestId,
      issueDescription: draft.issueDescription || prev.issueDescription,
    }))

    setAppliedDraftRequestId(hydratedFromRequestId)
  }, [appliedDraftRequestId, draft, hydratedFromRequestId])

  useEffect(() => {
    void bootstrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!serviceTypesClientCode) {
      setServiceParents([])
      setServiceChildren([])
      setServiceParentId('')
      setServiceTypesLoading(false)
      return
    }

    let cancelled = false
    setServiceTypesLoading(true)
    apiClient
      .get('/options/service-types', {
        headers: { 'X-Client-Code': serviceTypesClientCode },
      })
      .then((res) => {
        if (cancelled) return
        const rows = Array.isArray(res.data) ? res.data : []
        setServiceParents(
          rows
            .filter((row: { parent_id?: number | null }) => !row.parent_id)
            .map((row: { id: number; name?: string; code?: string }) => ({
              id: Number(row.id),
              name: String(row.name ?? row.id),
              code: row.code ? String(row.code) : undefined,
            })),
        )
        setServiceChildren(
          rows
            .filter((row: { parent_id?: number | null }) => row.parent_id)
            .map((row: { id: number; name?: string; code?: string; parent_id?: number }) => ({
              id: Number(row.id),
              name: String(row.name ?? row.id),
              code: row.code ? String(row.code) : undefined,
              parent_id: Number(row.parent_id),
            })),
        )
      })
      .catch(() => {
        if (!cancelled) {
          setServiceParents([])
          setServiceChildren([])
        }
      })
      .finally(() => {
        if (!cancelled) setServiceTypesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [serviceTypesClientCode])

  useEffect(() => {
    if (!form.serviceTypeId || (!serviceParents.length && !serviceChildren.length)) return

    const id = form.serviceTypeId
    const child = serviceChildren.find((st) => st.id === id)
    if (child) {
      setServiceParentId(String(child.parent_id))
      return
    }
    const parent = serviceParents.find((st) => st.id === id)
    if (parent) {
      setServiceParentId(String(parent.id))
    }
  }, [form.serviceTypeId, serviceParents, serviceChildren])

  useEffect(() => {
    if (!form.serviceTypeId) return
    const meta = resolveServiceMeta(form.serviceTypeId, serviceParents, serviceChildren)
    const derivedTier = deriveTierFromServiceCode(meta.code)
    if (!derivedTier) return
    setForm((prev) => {
      if (prev.supportType?.trim()) return prev
      return { ...prev, supportType: derivedTier }
    })
  }, [form.serviceTypeId, serviceParents, serviceChildren])

  // Create mode: generate unique ticket ID once so agent can copy/send before submit
  useEffect(() => {
    if (parentTicketId) return
    setForm((prev) => ({
      ...prev,
      ticketId: prev.ticketId || generateTicketId(),
    }))
  }, [parentTicketId])

  // Edit mode: populate form from ticket once per ticket id (avoid wipe on mergeTicket after clear/persist)
  const syncFormFromTicket = useCallback(
    (ticket: ParentTicketWithMeta, ticketId: string) => {
      const values = ticketToFormValues(
        ticket as Parameters<typeof ticketToFormValues>[0],
        ticketId,
      )
      if (!values) return
      // Skip undefined so a null API field never wipes a value the user already set.
      const defined = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== undefined),
      ) as Partial<ParentTicketFormValues>
      setForm((prev) => ({ ...prev, ...defined, clientPurchaseOrderId: values.clientPurchaseOrderId ?? null }))
      const ext = ticket as Record<string, unknown>
      const poFromTicket = ext.purchaseOrder as ClientPurchaseOrder | undefined
      if (poFromTicket?.id) {
        setPurchaseOrders((prev) =>
          prev.some((po) => po.id === poFromTicket.id) ? prev : [poFromTicket, ...prev],
        )
      }
      if (ticket.requestId) {
        setRequestSearchTerm(
          requestLabel({
            id: ticket.requestId,
            clientName: ticket.clientName,
            clientOrg: ticket.clientOrg,
            requestedAt: ticket.requestedAt,
          }),
        )
      }
      if (ticket.agentName) setAgentSearchTerm(ticket.agentName)
    },
    [],
  )

  useEffect(() => {
    if (!parentTicketId || !editTicket) return
    if (!shouldHydrateFormFromTicket(parentTicketId, formHydratedTicketIdRef.current)) return
    syncFormFromTicket(editTicket as ParentTicketWithMeta, parentTicketId)
    formHydratedTicketIdRef.current = String(parentTicketId)
  }, [parentTicketId, editTicket, syncFormFromTicket])

  // Edit mode: set selected agent from ticket.agent when present so "Agent" block displays on first load
  useEffect(() => {
    if (!parentTicketId || !editTicket) return
    const agent = (editTicket as { agent?: AgentOption }).agent
    if (!agent || typeof agent !== 'object' || !('id' in agent)) return
    setSelectedAgentOption(agent)
    setAgentSearchTerm(agentLabel(agent))
    const isCurrentUser = currentUser && Number(agent.id) === currentUser.id
    setAssignToMe(!!isCurrentUser)
  }, [parentTicketId, editTicket, currentUser?.id])

  // Edit mode: enable dispatch panel when ticket has FE assignment or schedules
  useEffect(() => {
    if (!parentTicketId || !editTicket) return
    if (dispatchOptOutRef.current) return
    const meta = editTicket as ParentTicketWithMeta & {
      primary_engineer_id?: number | null
      primaryEngineerId?: number | null
    }
    const hasSchedules = Array.isArray(meta.feSchedules) && meta.feSchedules.length > 0
    const hasEngineer =
      Boolean(meta.primaryEngineer) ||
      meta.primary_engineer_id != null ||
      meta.primaryEngineerId != null
    if (meta.dispatchRequired ?? meta.dispatchPlan?.required ?? (hasSchedules || hasEngineer)) {
      setRequiresDispatch(true)
      const readiness = meta.dispatchPlan?.readiness ?? (hasSchedules || hasEngineer ? 'ready' : 'pending')
      setDispatchReadiness(readiness)
      setDispatchHoldReason(meta.dispatchPlan?.holdReason ?? '')
      setDispatchHoldRemarks(meta.dispatchPlan?.holdRemarks ?? '')
      setDispatchExpectedFollowUp(meta.dispatchPlan?.expectedFollowUp ?? '')
    }
  }, [parentTicketId, editTicket])

  // Edit mode: hydrate confirmed dispatch from existing store + primary FE schedule
  useEffect(() => {
    if (!parentTicketId || !editTicket || dispatchHydratedRef.current) return
    if (dispatchOptOutRef.current) return
    const meta = editTicket as ParentTicketWithMeta
    const { primary, additionalFes } = buildDispatchSelectionsFromTicket(meta)
    if (!primary) return
    dispatchSelectionRef.current = primary
    setDispatchSelection(primary)
    setDispatchAdditionalSelections(additionalFes)
    lastPersistedDispatchSnapshotRef.current = dispatchSelectionSnapshot(primary, additionalFes, {
      certificationRequired: Boolean(meta.certificationRequired ?? form.certificationRequired),
      brandId: meta.brandId ?? form.brandId ?? null,
    })
    dispatchHydratedRef.current = true
  }, [parentTicketId, editTicket])

  useEffect(() => {
    dispatchHydratedRef.current = false
    formHydratedTicketIdRef.current = null
    dispatchSelectionRef.current = null
    lastPersistedDispatchSnapshotRef.current = ''
    dispatchOptOutRef.current = false
  }, [parentTicketId])

  // Edit mode: load and select the ticket request
  useEffect(() => {
    if (!parentTicketId || !editTicket?.requestId) return

    const ticketWithRequest = editTicket as { request?: RequestOption }
    if (ticketWithRequest.request && typeof ticketWithRequest.request === 'object' && 'id' in ticketWithRequest.request) {
      const normalized = mapApiRequestToRequestOption(
        ticketWithRequest.request as unknown as Record<string, unknown>,
      )
      setSelectedRequestOption(normalized)
      setRequestSearchTerm(requestLabel(normalized))
      return
    }

    const store = (editTicket as ParentTicketWithMeta).store
    const syntheticRequest: RequestOption = {
      id: editTicket.requestId,
      clientName: editTicket.clientName,
      clientOrg: editTicket.clientOrg ?? undefined,
      requestedAt: editTicket.requestedAt,
      issueDescription: editTicket.description ?? undefined,
      description: editTicket.description ?? undefined,
      siteAddress:
        resolveSeedLocationAddress({
          requestSiteAddress: undefined,
          incidentLocationAddress:
            (editTicket as { incidentLocationAddress?: string }).incidentLocationAddress ??
            (editTicket as ParentTicketWithMeta).incidentLocation?.address ??
            null,
          storeBranchAddress:
            store?.branchAddress ?? store?.locationAddress ?? store?.normalizedAddress ?? null,
        }) || undefined,
    }

    if (editTicket.requestId.length < 3) {
      setSelectedRequestOption(syntheticRequest)
      setRequestSearchTerm(requestLabel(syntheticRequest))
      return
    }

    let cancelled = false
    TicketService.searchRequestOptions(editTicket.requestId)
      .then((res) => {
        if (cancelled || !res.success || !res.data?.length) {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
          return
        }
        const match = res.data.find((r) => String(r.id) === String(editTicket.requestId))
        const selected = match
          ? mapApiRequestToRequestOption(match as unknown as Record<string, unknown>)
          : syntheticRequest
        setSelectedRequestOption(selected)
        setRequestSearchTerm(requestLabel(selected))
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
        }
      })
    return () => {
      cancelled = true
    }
  }, [
    parentTicketId,
    editTicket?.requestId,
    (editTicket as ParentTicketWithMeta | undefined)?.request,
  ])

  // Create mode: when redirected from IncomingRequest, same process as search + click – fetch request from API then set selection so details and fields match
  useEffect(() => {
    if (parentTicketId || !hydratedFromRequestId || !draft.requestId) return

    const syntheticRequest: RequestOption = {
      id: draft.requestId,
      clientName: draft.requestorName,
      clientOrg: draft.clientOrg || undefined,
      serialNumber: draft.serialNumber || undefined,
      unitModel: draft.unitModelRecord || undefined,
      requestedAt: draft.requestDatetime,
      issueDescription: draft.issueDescription || undefined,
      description: draft.issueDescription || undefined,
      submittedAt: draft.requestDatetime || undefined,
      serviceType: draft.serviceType || undefined,
      contactName: draft.contactName || undefined,
      contactEmail: draft.contactEmail || undefined,
      requestSource: draft.requestSource || undefined,
    }

    if (draft.requestId.length < 3) {
      setSelectedRequestOption(syntheticRequest)
      setRequestSearchTerm(requestLabel(syntheticRequest))
      return
    }

    let cancelled = false
    TicketService.searchRequestOptions(draft.requestId)
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data?.length) {
          const match = res.data.find((r) => String(r.id) === String(draft.requestId))
          const selected = match ?? syntheticRequest
          setSelectedRequestOption(selected)
          setRequestSearchTerm(requestLabel(selected))
          updateField('requestId', selected.id)
          if (selectedRequestDetailsRef.current) {
            setTimeout(() => {
              selectedRequestDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }, 50)
          }
        } else {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
          updateField('requestId', syntheticRequest.id)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
          updateField('requestId', syntheticRequest.id)
        }
      })
    return () => {
      cancelled = true
    }
  }, [parentTicketId, hydratedFromRequestId, draft.requestId])

  const bootstrap = async () => {
    try {
      setLoading(true)
      // No need to load all clients upfront - we'll search on demand
    } catch (e: any) {
      showError(e?.message || 'Failed to load ticket data')
    } finally {
      setLoading(false)
    }
  }

  // Debounced request search function
  const searchRequests = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setRequestSearchResults([])
      // Clear selected request when search term is too short
      setSelectedRequestOption(null)
      return
    }

    setIsRequestSearching(true)
    try {
      const response = await TicketService.searchRequestOptions(searchTerm)
      if (response.success && response.data && response.data.length > 0) {
        setRequestSearchResults(response.data)
      } else {
        setRequestSearchResults([])
        // Clear selected request when search returns empty results
        setSelectedRequestOption(null)
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to search requests')
      setRequestSearchResults([])
      setSelectedRequestOption(null)
    } finally {
      setIsRequestSearching(false)
    }
  }, [showError])

  // Handle request search input change
  const handleRequestSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setRequestSearchTerm(value)

    // Clear previous timeout
    if (requestSearchTimeoutRef.current) {
      clearTimeout(requestSearchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    requestSearchTimeoutRef.current = setTimeout(() => {
      searchRequests(value)
    }, 300)
  }

  // Handle request selection (from search dropdown click) – show "Selected Request Details" and scroll it into view
  const handleRequestSelect = (request: RequestOption) => {
    setPendingClient(null)
    const prevClientId = effectiveClientId
    if (request.clientId !== prevClientId) {
      loadedPurchaseOrdersClientIdRef.current = null
      setPurchaseOrders([])
      setForm((prev) => ({
        ...prev,
        clientPurchaseOrderId: null,
        certificationRequired: false,
        brandId: null,
      }))
    }
    setSelectedRequestOption(request)
    updateField('requestId', request.id)
    if (request.serviceTypeId != null) {
      updateField('serviceTypeId', request.serviceTypeId)
    }
    setRequestSearchTerm(requestLabel(request))
    setRequestSearchResults([])
    requestSearchInputRef.current?.blur()
    setTimeout(() => {
      selectedRequestDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 50)
  }

  // Agent search (Helpdesk users)
  const searchAgents = useCallback(async (searchTerm: string) => {
    if (!canAssignAgent) {
      setAgentSearchResults([])
      return
    }
    if (searchTerm.length < 2) {
      setAgentSearchResults([])
      setSelectedAgentOption(null)
      return
    }
    setIsAgentSearching(true)
    try {
      const response = await TicketService.searchHelpdeskAgentOptions(searchTerm)
      if (response.success && response.data?.length) {
        setAgentSearchResults(response.data)
      } else {
        setAgentSearchResults([])
        setSelectedAgentOption(null)
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to search agents')
      setAgentSearchResults([])
      setSelectedAgentOption(null)
    } finally {
      setIsAgentSearching(false)
    }
  }, [canAssignAgent, showError])

  const handleAgentSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canAssignAgent) return
    const value = e.target.value
    setAgentSearchTerm(value)
    if (agentSearchTimeoutRef.current) clearTimeout(agentSearchTimeoutRef.current)
    agentSearchTimeoutRef.current = setTimeout(() => searchAgents(value), 300)
  }

  const handleAgentSelect = (agent: AgentOption) => {
    if (!canAssignAgent) return
    setAssignToMe(false)
    setSelectedAgentOption(agent)
    updateField('agentId', String(agent.id))
    setAgentSearchTerm(agentLabel(agent))
    setAgentSearchResults([])
  }

  const currentUserDisplayName = currentUser
    ? [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ').trim() ||
      currentUser.name ||
      `User #${currentUser.id}`
    : ''

  // Create: default agent to the user creating the ticket
  useEffect(() => {
    if (isEditMode || !currentUser || creatorAgentSeededRef.current) return
    creatorAgentSeededRef.current = true
    setAssignToMe(true)
    setSelectedAgentOption(null)
    setAgentSearchResults([])
    setAgentSearchTerm(currentUserDisplayName)
    setForm((prev) => (prev.agentId ? prev : { ...prev, agentId: String(currentUser.id) }))
  }, [isEditMode, currentUser, currentUserDisplayName])

  const handleAssignToMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canAssignAgent) return
    const checked = e.target.checked
    setAssignToMe(checked)
    if (checked && currentUser) {
      updateField('agentId', String(currentUser.id))
      setAgentSearchTerm(currentUserDisplayName)
      setSelectedAgentOption(null)
      setAgentSearchResults([])
    } else {
      updateField('agentId', '')
      setAgentSearchTerm('')
      setSelectedAgentOption(null)
    }
  }

  // Load purchase orders when client changes
  useEffect(() => {
    const clientId = effectiveClientId
    if (!clientId) {
      setPurchaseOrders([])
      setIsLoadingPurchaseOrders(false)
      setPurchaseOrderLoadError(null)
      loadedPurchaseOrdersClientIdRef.current = null
      return
    }

    if (loadedPurchaseOrdersClientIdRef.current === clientId) {
      return
    }

    let cancelled = false
    setIsLoadingPurchaseOrders(true)
    setPurchaseOrderLoadError(null)

    ClientService.getClientPurchaseOrders(clientId)
      .then((response) => {
        if (cancelled) return
        if (response.success && response.data) {
          setPurchaseOrders((previous) => {
            const saved = previous.find((po) => po.id === form.clientPurchaseOrderId && po.client_id === clientId)
            return saved && !response.data!.some((po) => po.id === saved.id) ? [saved, ...response.data!] : response.data!
          })
          loadedPurchaseOrdersClientIdRef.current = clientId
        } else {
          setPurchaseOrders([])
          setPurchaseOrderLoadError(response.message ?? 'Failed to load purchase orders')
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message = error instanceof Error ? error.message : 'Failed to load purchase orders'
        setPurchaseOrders([])
        setPurchaseOrderLoadError(message)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPurchaseOrders(false)
      })

    return () => {
      cancelled = true
    }
  }, [effectiveClientId, purchaseOrderRefresh, form.clientPurchaseOrderId])

  const selectedPurchaseOrder = useMemo(
    () => purchaseOrders.find((po) => po.id === form.clientPurchaseOrderId) ?? null,
    [purchaseOrders, form.clientPurchaseOrderId],
  )

  const contextAgentLabel = useMemo(() => {
    if (assignToMe && currentUser) return currentUserDisplayName
    if (selectedAgentOption) return agentLabel(selectedAgentOption)
    if (form.agentId && agentSearchTerm) return agentSearchTerm
    return ''
  }, [assignToMe, currentUser, currentUserDisplayName, selectedAgentOption, form.agentId, agentSearchTerm])

  const geographicLabel = useCallback(
    (options: GeographicOption[], id?: number) =>
      options.find((option) => option.id === id)?.label ?? null,
    [],
  )

  const ticketDetailsPreview = useMemo(() => {
    const serviceTypeName = form.serviceTypeId
      ? serviceChildren.find((st) => st.id === form.serviceTypeId)?.name
        ?? serviceParents.find((st) => st.id === form.serviceTypeId)?.name
        ?? null
      : null

    return {
      severity: severitySelectOptions.find((option) => option.code === severitySelectValue)?.label ?? null,
      serviceType: serviceTypeName,
      clientReferenceNumber: form.clientReferenceNumber?.trim() || null,
    }
  }, [
    form.serviceTypeId,
    form.clientReferenceNumber,
    severitySelectOptions,
    severitySelectValue,
    serviceChildren,
    serviceParents,
  ])

  const locationPreview = useMemo(
    () => ({
      region: geographicLabel(regionOptions, form.regionId),
      areaCluster: geographicLabel(areaClusterOptions, form.areaClusterId),
      province: geographicLabel(provinceOptions, form.provinceId),
      town: geographicLabel(townOptions, form.townId),
    }),
    [
      geographicLabel,
      regionOptions,
      areaClusterOptions,
      provinceOptions,
      townOptions,
      form.regionId,
      form.areaClusterId,
      form.provinceId,
      form.townId,
    ],
  )

  const dispatchLocationFilter = useMemo(
    () => ({
      areaClusterId: form.areaClusterId ?? null,
      region: locationPreview.region,
      province: locationPreview.province,
    }),
    [form.areaClusterId, locationPreview.region, locationPreview.province],
  )

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (requestSearchTimeoutRef.current) clearTimeout(requestSearchTimeoutRef.current)
      if (agentSearchTimeoutRef.current) clearTimeout(agentSearchTimeoutRef.current)
    }
  }, [])


  const updateField = <K extends keyof ParentTicketFormValues>(
    key: K,
    value: ParentTicketFormValues[K],
  ) => {
    markFormDirty()
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const addPartRow = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      parts: [...(prev.parts ?? []), { part_name: '', quantity: '', unit: '' }],
    }))
  }, [])

  const updatePartRow = useCallback((
    index: number,
    field: keyof TicketPartFormRow,
    value: string | number | undefined,
  ) => {
    setForm((prev) => ({
      ...prev,
      parts: (prev.parts ?? []).map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    }))
  }, [])

  const removePartRow = useCallback((index: number) => {
    setForm((prev) => {
      const row = (prev.parts ?? [])[index]
      if (row?.addedAsTask) {
        showError(new Error('This part is linked to a procurement task — remove it from the task first.'))
        return prev
      }
      return {
        ...prev,
        parts: (prev.parts ?? []).filter((_, i) => i !== index),
      }
    })
  }, [showError])

  const setHasPartsNeeded = useCallback((checked: boolean) => {
    if (!checked) {
      const linked = (form.parts ?? []).filter((p) => p.addedAsTask)
      if (linked.length > 0) {
        setLinkedPartsForConfirm(
          linked.map((p) => ({
            part_name: p.part_name,
            child_ticket_id: p.childTicketId,
            taskNumber: p.taskNumber,
          })),
        )
        openConfirmUncheck()
        return
      }
      setForm((prev) => ({
        ...prev,
        hasPartsNeeded: false,
        parts: [],
        confirmRemoveLinkedParts: false,
      }))
      return
    }

    setForm((prev) => ({
      ...prev,
      hasPartsNeeded: true,
      confirmRemoveLinkedParts: false,
      parts: prev.parts?.length ? prev.parts : [{ part_name: '', quantity: '', unit: '' }],
    }))
  }, [form.parts, openConfirmUncheck])

  const confirmRemoveLinkedPartsAnyway = useCallback(() => {
    closeConfirmUncheck()
    setForm((prev) => ({
      ...prev,
      hasPartsNeeded: false,
      parts: [],
      confirmRemoveLinkedParts: true,
    }))
  }, [closeConfirmUncheck])

  const addAttachmentFiles = useCallback((files: File[]) => {
    if (!files.length) return
    setForm((prev) => ({
      ...prev,
      hasAttachmentsNeeded: true,
      attachmentRows: [
        ...(prev.attachmentRows ?? []),
        ...files.map((file) => ({
          attachmentType: 'other' as const,
          file,
          fileName: file.name,
        })),
      ],
    }))
  }, [])

  const removeAttachmentRow = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      attachmentRows: (prev.attachmentRows ?? []).filter((_, i) => i !== index),
    }))
  }, [])

  const setHasAttachmentsNeeded = useCallback((checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      hasAttachmentsNeeded: checked,
      attachmentRows: checked
        ? (prev.attachmentRows?.length
          ? prev.attachmentRows
          : [])
        : [],
    }))
  }, [])

  // Auto-populate from selected request: subject (Request ID | Contact Name | Store), source, description
  useEffect(() => {
    if (!selectedRequestOption) return
    const subject = buildTicketRequestSubject({
      request_number: selectedRequestOption.id,
      contactName: selectedRequestOption.contactName,
      clientName: selectedRequestOption.clientName,
      clientOrg: selectedRequestOption.clientOrg,
    })
    setForm((prev) => ({
      ...prev,
      subject: subject || prev.subject,
      source: normalizeTicketSource(selectedRequestOption.requestSource) || prev.source,
      serviceTypeId: selectedRequestOption.serviceTypeId ?? prev.serviceTypeId,
      severity: prev.severity ?? selectedRequestOption.priority ?? prev.priority,
      assignedTeamId: prev.assignedTeamId ?? selectedRequestOption.assignedTeamId ?? prev.assignedTeamId,
      scheduledAt: prev.scheduledAt || selectedRequestOption.preferredScheduleAt,
      issueDescription:
        prev.issueDescription || (selectedRequestOption.issueDescription ?? selectedRequestOption.description ?? ''),
    }))

    if (canAssignAgent && selectedRequestOption.assignedTo) {
      updateField('agentId', String(selectedRequestOption.assignedTo))
      if (selectedRequestOption.assignedToName) {
        setAgentSearchTerm(selectedRequestOption.assignedToName)
      }
      setAssignToMe(false)
    }

    setPrefilledFields((prev) => ({
      ...prev,
      subject: Boolean(subject),
      source: Boolean(selectedRequestOption.requestSource),
      serviceTypeId: selectedRequestOption.serviceTypeId != null,
      severity: selectedRequestOption.priority != null,
      assignedTeamId: selectedRequestOption.assignedTeamId != null,
      issueDescription: Boolean(selectedRequestOption.issueDescription ?? selectedRequestOption.description),
      agentId: Boolean(canAssignAgent && selectedRequestOption.assignedTo),
    }))
  }, [selectedRequestOption?.id, canAssignAgent])


  useEffect(() => {
    let cancelled = false
    setGeographicLoading(true)
    geographicService.getRegions()
      .then((regions) => {
        if (cancelled) return
        setRegionOptions(regions)
      })
      .catch(() => {
        if (!cancelled) {
          setRegionOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) setGeographicLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!form.regionId) { setAreaClusterOptions([]); return }
    let cancelled = false
    geographicService.getAreaClusters(form.regionId).then((rows) => { if (!cancelled) setAreaClusterOptions(rows) }).catch(() => { if (!cancelled) setAreaClusterOptions([]) })
    return () => { cancelled = true }
  }, [form.regionId])

  useEffect(() => {
    if (!form.areaClusterId) {
      setProvinceOptions([])
      setProvinceLoading(false)
      return
    }

    let cancelled = false
    setProvinceLoading(true)
    geographicService
      .getProvincesByCluster(form.areaClusterId)
      .then((rows) => {
        if (!cancelled) setProvinceOptions(rows)
      })
      .catch(() => {
        if (!cancelled) setProvinceOptions([])
      })
      .finally(() => {
        if (!cancelled) setProvinceLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [form.areaClusterId])

  useEffect(() => {
    if (!form.provinceId) {
      setTownOptions([])
      setTownLoading(false)
      return
    }

    let cancelled = false
    setTownLoading(true)
    geographicService
      .getTownsByProvince(form.provinceId)
      .then((rows) => {
        if (!cancelled) setTownOptions(rows)
      })
      .catch(() => {
        if (!cancelled) setTownOptions([])
      })
      .finally(() => {
        if (!cancelled) setTownLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [form.provinceId])

  const editInitialStoreId = useMemo(() => {
    if (!isEditMode) return undefined
    const raw = form.storeId ?? (editTicket as ParentTicketWithMeta | undefined)?.store?.storeId
    if (raw == null || raw === '') return undefined
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
  }, [isEditMode, form.storeId, editTicket])

  const editInitialStore = useMemo(() => {
    if (!isEditMode) return undefined
    return (editTicket as ParentTicketWithMeta | undefined)?.store ?? undefined
  }, [isEditMode, editTicket])

  const editInitialLocation = useMemo<IncidentLocation | null>(() => {
    if (!isEditMode || form.incidentLocationType !== 'location') return null
    if (form.incidentLocationLatitude == null || form.incidentLocationLongitude == null) return null
    return {
      type: 'location',
      name: form.incidentLocationName,
      address: form.incidentLocationAddress,
      latitude: form.incidentLocationLatitude,
      longitude: form.incidentLocationLongitude,
      placeId: form.incidentLocationPlaceId,
      source: form.incidentLocationSource,
      municipality: form.incidentLocationMunicipality,
      province: form.incidentLocationProvince,
      country: form.incidentLocationCountry,
    }
  }, [
    isEditMode,
    form.incidentLocationType,
    form.incidentLocationName,
    form.incidentLocationAddress,
    form.incidentLocationLatitude,
    form.incidentLocationLongitude,
    form.incidentLocationPlaceId,
    form.incidentLocationSource,
    form.incidentLocationMunicipality,
    form.incidentLocationProvince,
    form.incidentLocationCountry,
  ])

  const seedLocationAddress = useMemo(() => {
    const storeBranchAddress =
      editInitialStore?.branchAddress ??
      editInitialStore?.locationAddress ??
      editInitialStore?.normalizedAddress ??
      editInitialStore?.location?.address ??
      null
    return resolveSeedLocationAddress({
      requestSiteAddress: selectedRequestOption?.siteAddress,
      incidentLocationAddress: form.incidentLocationAddress,
      storeBranchAddress,
    })
  }, [
    selectedRequestOption?.siteAddress,
    form.incidentLocationAddress,
    editInitialStore,
  ])

  const hasPersistedDispatch = useMemo(() => {
    if (!isEditMode || !editTicket) return false
    const meta = editTicket as ParentTicketWithMeta & {
      primary_engineer_id?: number | null
      primaryEngineerId?: number | null
    }
    const hasSchedules = Array.isArray(meta.feSchedules) && meta.feSchedules.length > 0
    const hasEngineer =
      Boolean(meta.primaryEngineer) ||
      meta.primary_engineer_id != null ||
      meta.primaryEngineerId != null
    return hasSchedules || hasEngineer
  }, [isEditMode, editTicket])

  const isDispatchReadOnly = useMemo(
    () => isEditMode && isTerminalParentStatus((editTicket as ParentTicketWithMeta | undefined)?.status),
    [isEditMode, editTicket],
  )

  const procurementTaskCount = useMemo(
    () => countTasksBySourceField(ticketTasks, 'tools_parts'),
    [ticketTasks],
  )
  const duplicatePartIndexes = useMemo(
    () => getDuplicatePartIndexes(form.parts),
    [form.parts],
  )
  const documentTaskCount = useMemo(
    () => countTasksBySourceField(ticketTasks, 'attachments'),
    [ticketTasks],
  )

  const refreshTicketTasks = useCallback(async () => {
    if (!parentTicketId) {
      setTicketTasks([])
      return
    }
    try {
      const list = await childTicketService.list(parentTicketId)
      setTicketTasks(list)
    } catch {
      setTicketTasks([])
    }
  }, [parentTicketId])

  useEffect(() => {
    void refreshTicketTasks()
  }, [refreshTicketTasks])

  useEffect(() => {
    if (!isEditMode) return
    let cancelled = false
    apiClient
      .get<{ data: Array<{ code: string; scope?: string | null; is_terminal?: boolean }> }>(
        '/service-catalog/child-ticket-statuses',
        { params: { per_page: 100 } },
      )
      .then((res) => {
        if (cancelled) return
        const items = res.data?.data
        if (Array.isArray(items)) {
          window.localStorage.setItem(
            'helpdesk.child_ticket_status_catalog',
            JSON.stringify(
              items.map((item) => ({
                code: item.code,
                scope: item.scope ?? null,
                is_terminal: item.is_terminal === true,
              })),
            ),
          )
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isEditMode])

  const handleCreateFieldTask = useCallback(
    (sourceField: TaskSourceField) => {
      if (!parentTicketId) return
      setEditingTask(null)
      setTaskCreateDefaults(defaultCreatePayloadForSourceField(sourceField))
      openTaskDrawer()
    },
    [openTaskDrawer, parentTicketId],
  )

  const handleOpenFieldTask = useCallback(
    (task: ChildTicketTask) => {
      setTaskCreateDefaults(null)
      setEditingTask(task)
      openTaskDrawer()
    },
    [openTaskDrawer],
  )

  const handleTaskDrawerClose = useCallback(() => {
    setEditingTask(null)
    setTaskCreateDefaults(null)
    closeTaskDrawer()
  }, [closeTaskDrawer])

  const handleTaskSaved = useCallback(async () => {
    await refreshTicketTasks()
  }, [refreshTicketTasks])

  /** When editing, resolve primary/backup FE schedule IDs for persist/clear (parent-level, slot-aligned). */
  const dispatchExistingScheduleIds = useMemo(
    () =>
      resolveDispatchScheduleIds(
        editTicket as ParentTicketWithMeta | undefined,
        dispatchSelection,
        dispatchAdditionalSelections,
      ),
    [editTicket, dispatchSelection, dispatchAdditionalSelections],
  )
  const existingPrimaryScheduleId = dispatchExistingScheduleIds.primary
  const existingAdditionalScheduleIds = dispatchExistingScheduleIds.additionalFes

  const dispatchSnapshotContext = useMemo(
    () => ({
      certificationRequired: Boolean(form.certificationRequired),
      brandId: form.brandId ?? null,
      requiredCertifications:
        form.certificationRequired && form.brandId != null
          ? [String(form.brandId)]
          : [],
      activityFromLocal: dispatchSelection
        ? `${dispatchSelection.scheduleDate}T${dispatchSelection.scheduleTime}`
        : '',
      activityToLocal: dispatchSelection
        ? `${dispatchSelection.scheduleEndDate ?? dispatchSelection.scheduleDate}T${dispatchSelection.scheduleEndTime}`
        : '',
    }),
    [form.certificationRequired, form.brandId, dispatchSelection],
  )

  const handleDispatchConfirm = useCallback((selection: FEDispatchSelection) => {
    markFormDirty()
    if (selection.role === 'additional') {
      const slot = selection.additionalSlot ?? 1
      setDispatchAdditionalSelections((prev) => {
        const next = [...prev]
        next[slot - 1] = selection
        return next
      })
    } else {
      dispatchSelectionRef.current = selection
      setDispatchSelection(selection)
      setDispatchWindow({
        from: `${selection.scheduleDate}T${selection.scheduleTime}`,
        to: `${selection.scheduleEndDate ?? selection.scheduleDate}T${selection.scheduleEndTime}`,
      })
      setForm((prev) => {
        if (selection.locationType === 'location') {
          const name = selection.locationDisplayName ?? selection.storeName
          return {
            ...prev,
            storeId: '',
            storeCode: '',
            incidentLocationType: 'location',
            incidentLocationName: name,
            incidentLocationAddress: selection.storeAddress || name,
            incidentLocationLatitude: selection.latitude,
            incidentLocationLongitude: selection.longitude,
            incidentLocationSource: selection.locationSource ?? 'nominatim',
            incidentLocationMunicipality: selection.incidentLocationMunicipality ?? prev.incidentLocationMunicipality ?? null,
            incidentLocationProvince: selection.incidentLocationProvince ?? prev.incidentLocationProvince ?? null,
            incidentLocationCountry: selection.incidentLocationCountry ?? prev.incidentLocationCountry ?? null,
          }
        }
        return {
          ...prev,
          storeId: selection.storeId ?? undefined,
          storeCode: selection.storeCode ?? prev.storeCode ?? '',
          incidentLocationType: 'store',
          incidentLocationName: selection.storeName,
          incidentLocationAddress: selection.storeAddress,
          incidentLocationLatitude: selection.latitude,
          incidentLocationLongitude: selection.longitude,
          incidentLocationSource: selection.locationSource ?? 'store',
          ...(selection.locationSource === 'manual_pin'
            ? {
                incidentLocationMunicipality: selection.incidentLocationMunicipality ?? prev.incidentLocationMunicipality ?? null,
                incidentLocationProvince: selection.incidentLocationProvince ?? prev.incidentLocationProvince ?? null,
                incidentLocationCountry: selection.incidentLocationCountry ?? prev.incidentLocationCountry ?? null,
              }
            : clearIncidentLocationGeoFields()),
        }
      })
    }
  }, [markFormDirty])

  const handleDispatchStoreSelected = useCallback((store: ClientOption) => {
    setForm((prev) => ({
      ...prev,
      ...storeFieldsFromClientOption(store),
    }))
  }, [])

  const handleDispatchLocationSelected = useCallback((location: FreeTextLocationResult | null) => {
    if (!location) {
      setForm((prev) => ({
        ...prev,
        storeId: undefined,
        storeCode: '',
        incidentLocationType: '',
        incidentLocationName: '',
        incidentLocationAddress: '',
        incidentLocationLatitude: null,
        incidentLocationLongitude: null,
        incidentLocationPlaceId: null,
        incidentLocationSource: null,
        ...clearIncidentLocationGeoFields(),
      }))
      return
    }
    setForm((prev) => ({
      ...prev,
      storeId: '',
      storeCode: '',
      ...incidentLocationFieldsFromNominatim(location),
    }))
  }, [])

  const clearDispatchLocalState = useCallback(() => {
    dispatchSelectionRef.current = null
    setDispatchSelection(null)
    setDispatchAdditionalSelections([])
    setSitePinConfirmed(false)
    lastPersistedDispatchSnapshotRef.current = ''
    dispatchHydratedRef.current = true
    setForm((prev) => ({
      ...prev,
      ...clearedDispatchContextFields(),
      // Preserve non-dispatch fields (Issue classification + Location groups).
      regionId: prev.regionId,
      areaClusterId: prev.areaClusterId,
      provinceId: prev.provinceId,
      townId: prev.townId,
      issueCategoryId: prev.issueCategoryId,
      issueSubCategoryId: prev.issueSubCategoryId,
      issueItemId: prev.issueItemId,
    }))
  }, [])

  const handleDispatchCheckboxChange = useCallback(
    async (checked: boolean) => {
      if (checked) {
        dispatchOptOutRef.current = false
        setRequiresDispatch(true)
        const { nextPrimary, nextAdditionalFes } = resolveDispatchSelectionsOnToggle({
          checked: true,
          isEditMode,
          hasPersistedDispatch,
          editTicket: editTicket as ParentTicketWithMeta | undefined,
          currentPrimary: dispatchSelection,
          currentAdditionalFes: dispatchAdditionalSelections,
        })
        markFormDirty()
        dispatchSelectionRef.current = nextPrimary
        setDispatchSelection(nextPrimary)
        setDispatchAdditionalSelections(nextAdditionalFes)
        return
      }

      // Uncheck stages withdrawal; no operational schedules or locks change here.
      dispatchOptOutRef.current = true
      setRequiresDispatch(false)
      setForm((prev) => ({
        ...prev,
        certificationRequired: false,
        brandId: null,
        // Preserve non-dispatch fields (Issue classification + Location groups).
        regionId: prev.regionId,
        areaClusterId: prev.areaClusterId,
        provinceId: prev.provinceId,
        townId: prev.townId,
        issueCategoryId: prev.issueCategoryId,
        issueSubCategoryId: prev.issueSubCategoryId,
        issueItemId: prev.issueItemId,
      }))

      markFormDirty()
      clearDispatchLocalState()
      showSuccess(
        'Dispatch removal proposed',
        'Save the parent ticket to apply this change. Existing work has not changed.',
      )
    },
    [
      isEditMode,
      parentTicketId,
      hasPersistedDispatch,
      dispatchSelection,
      dispatchAdditionalSelections,
      editTicket,
      form.storeId,
      form.storeCode,
      form.incidentLocationName,
      form.incidentLocationAddress,
      markFormDirty,
      clearDispatchLocalState,
      showError,
      showSuccess,
    ],
  )

  const handleDispatchStoreClear = useCallback(() => {
    markFormDirty()
    dispatchSelectionRef.current = null
    setDispatchSelection(null)
    setDispatchAdditionalSelections([])
    setForm((prev) => ({
      ...prev,
      storeId: undefined,
      storeCode: '',
      incidentLocationType: '',
      incidentLocationName: '',
      incidentLocationAddress: '',
      incidentLocationLatitude: null,
      incidentLocationLongitude: null,
      incidentLocationPlaceId: null,
      incidentLocationSource: null,
      ...clearIncidentLocationGeoFields(),
    }))
  }, [markFormDirty])

  const focusFirstInvalidField = useCallback(() => {
    const checks: [boolean, RefObject<HTMLElement | null>][] = [
      [!form.requestId, requestSearchInputRef],
      [!(form.subject ?? '').trim(), subjectRef],
      [!(form.severity ?? form.priority ?? '').trim(), severityRef],
      [!(form.issueDescription ?? '').trim(), issueDescriptionRef],
      [!form.agentId, agentSearchInputRef],
    ]
    const first = checks.find(([invalid]) => invalid)
    if (first) {
      first[1].current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      first[1].current?.focus()
    }
  }, [form])

  const handleSubmit = async (submitTicket: boolean = false) => {
    if (saving) return
    const applyDispatch = submitTicket || isSubmittedTicket
    if (requiresDispatch && dispatchReadiness === 'ready' && hasUnresolvedInvalidDispatch && applyDispatch) {
      dispatchPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      showError(
        'Resolve invalid field engineer assignments before saving. Reassign or cancel dispatch for engineers marked as no longer eligible.',
      )
      return
    }

    if (requiresDispatch && dispatchReadiness === 'on_hold') {
      if (!dispatchHoldReason) {
        showError('Select a hold reason before submitting the ticket.')
        dispatchPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (!dispatchExpectedFollowUp || new Date(dispatchExpectedFollowUp).getTime() <= Date.now()) {
        showError('Expected follow-up must be a future date and time.')
        dispatchPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (dispatchHoldReason === 'other_client_dependency' && !dispatchHoldRemarks.trim()) {
        showError('Enter remarks when Other client dependency is selected.')
        dispatchPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
    }

    if (
      !validateParentTicketSubmit(form, (msg) => showError(msg), {
        applyDispatch,
        requiresDispatch,
        dispatchReadiness,
        hasDispatchSelection: Boolean(dispatchSelection),
        sitePinConfirmed,
        hasDispatchCoords: hasFiniteCoords(
          dispatchSelection?.latitude ?? form.incidentLocationLatitude,
          dispatchSelection?.longitude ?? form.incidentLocationLongitude,
        ),
      })
    ) {
      focusFirstInvalidField()
      if (requiresDispatch && dispatchReadiness === 'ready') {
        dispatchPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    const effectiveForm = buildEffectiveFormForSubmit(form, {
      requiresDispatch,
      dispatchSelection,
    })

    const mode = submitTicket ? 'submit' : 'draft'
    let lastValidationErrors: Record<string, string[]> = {}
    try {
      setSaving(true)
      setSavingMode(mode)
      setErrors({})
      setDispatchWarning(null)
      window.scrollTo({ top: 0, behavior: 'smooth' })

      const payload = buildMetadataPayload(effectiveForm, submitTicket, { requiresDispatch })
      if (selectedBillingClient) payload.clientId = selectedBillingClient.id
      payload.dispatchPlan = {
        required: requiresDispatch,
        readiness: requiresDispatch ? dispatchReadiness : undefined,
        holdReason: dispatchReadiness === 'on_hold' ? dispatchHoldReason : undefined,
        holdRemarks: dispatchReadiness === 'on_hold' ? dispatchHoldRemarks : undefined,
        expectedFollowUp: dispatchReadiness === 'on_hold' ? dispatchExpectedFollowUp : undefined,
        window: dispatchWindow ?? editTicket?.dispatchPlan?.window,
        assignments: requiresDispatch && dispatchReadiness === 'ready'
          ? [dispatchSelection, ...dispatchAdditionalSelections].filter((a): a is FEDispatchSelection => a != null)
          : [],
      }
      payload.expectedDispatchVersion = editTicket?.dispatchVersion
      payload.dispatchChangeReason = dispatchChangeReason
      let savedTicketId = parentTicketId ?? ''
      let successMessage = ''

      if (isEditMode && parentTicketId) {
        const res = await TicketService.updateParentTicket(parentTicketId, payload, {
          clientCode: effectiveClientCode,
        })
        if (!res.success) {
          if (res.code === 'linked_parts_removal_required' && res.linkedParts?.length) {
            setLinkedPartsForConfirm(
              res.linkedParts.map((p) => ({
                part_name: p.part_name,
                child_ticket_id: p.child_ticket_id,
              })),
            )
            openConfirmUncheck()
            return
          }
          if (res.errors) {
            lastValidationErrors = res.errors
            setErrors(res.errors)
          }
          throw new Error(res.message || 'Failed to update ticket')
        }
        setForm((prev) => ({ ...prev, confirmRemoveLinkedParts: false }))
        savedTicketId = parentTicketId
        successMessage = res.message || (isSubmittedTicket ? 'Changes applied' : submitTicket ? 'Ticket submitted' : 'Ticket saved as draft')
        const detailAfterSave = await TicketService.getParentTicket(parentTicketId)
        if (detailAfterSave.success && detailAfterSave.data) {
          const refreshed = detailAfterSave.data as ParentTicketWithMeta
          mergeTicket(refreshed)
          syncFormFromTicket(refreshed, parentTicketId)
        }
      } else {
        const res = await TicketService.createParentTicket(payload, {
          clientCode: effectiveClientCode,
        })
        if (!res.success) {
          if (res.errors) {
            lastValidationErrors = res.errors
            setErrors(res.errors)
          }
          throw new Error(res.message || 'Failed to create ticket')
        }
        const newTicketNumber = res.data?.ticketId
        const newNumericId = res.data?.id
        if (newTicketNumber) {
          setForm((prev) => ({ ...prev, ticketId: newTicketNumber }))
        }
        // Prefer numeric PK for subsequent API calls / navigation (ticketId is display number PT-…)
        savedTicketId = newNumericId != null ? String(newNumericId) : newTicketNumber || ''
        successMessage = newTicketNumber
          ? `${submitTicket ? 'Ticket submitted' : 'Ticket saved as draft'} — Ticket ID: ${newTicketNumber}`
          : submitTicket
            ? 'Ticket submitted'
            : 'Ticket saved as draft'
      }

      if (savedTicketId) {
        try {
          const attachmentResult = await TicketService.uploadParentTicketAttachments(savedTicketId, {
            rows: form.hasAttachmentsNeeded ? (form.attachmentRows ?? []) : [],
            source: 'form',
            replace: true,
          })
          if (!attachmentResult.success) {
            throw new Error(attachmentResult.message)
          }
          const detailRes = await TicketService.getParentTicket(savedTicketId)
          if (detailRes.success && detailRes.data) {
            mergeTicket(detailRes.data as ParentTicketWithMeta)
          }
        } catch (attachmentErr: unknown) {
          const detail =
            attachmentErr instanceof Error
              ? attachmentErr.message
              : 'Attachments could not be saved'
          showError(`Ticket saved, but attachments could not be saved — ${detail}`)
        }
      }

      showSuccess('Success', successMessage)

      setIsFormDirty(false)

      if (savedTicketId) {
        if (onSuccess) onSuccess(savedTicketId)
        else navigate(`/tickets/${savedTicketId}/view`)
      } else if (!isEditMode) {
        if (onSuccess) onSuccess('')
        else navigate('/tickets')
      }
    } catch (e: any) {
      const flatErrors = flattenValidationErrors(lastValidationErrors)
      const message =
        flatErrors[0] ||
        e?.message ||
        (isEditMode ? 'Failed to update ticket' : 'Failed to create ticket')
      showError(message)
      if (hasDispatchFieldErrors(lastValidationErrors)) {
        dispatchPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    } finally {
      setSaving(false)
      setSavingMode(null)
    }
  }

  // Input base props
  const inputBaseProps = {
    bg: 'white',
    borderColor: colors.inputBorder ?? 'gray.200',
    borderRadius: 'lg',
    _hover: { borderColor: colors.inputborderHover ?? colors.inputBorder ?? 'gray.300' },
    _focusVisible: {
      borderColor: colors.primary ?? 'blue.500',
      boxShadow: `0 0 0 1px ${colors.primary ?? '#3182ce'}`,
    },
  } as const

  const readyDispatchCoordsLat =
    dispatchSelection?.latitude ?? form.incidentLocationLatitude ?? null
  const readyDispatchCoordsLng =
    dispatchSelection?.longitude ?? form.incidentLocationLongitude ?? null
  const readyDispatchSubmitAllowed = isReadyDispatchSubmitAllowed({
    requiresDispatch,
    dispatchReadiness,
    sitePinConfirmed,
    hasDispatchSelection: Boolean(dispatchSelection),
    latitude: readyDispatchCoordsLat,
    longitude: readyDispatchCoordsLng,
    hasUnresolvedInvalidDispatch,
  })

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <VStack spacing={3}>
          <Spinner size="lg" color="blue.500" />
          <Text color={colors.text}>Loading ticket form...</Text>
        </VStack>
      </Box>
    )
  }

  return (
    <Box w="100%" minW={0} fontSize="13px">
      {dispatchWarning && (
        <Alert status="warning" borderRadius="md" mb={2.5}>
          <AlertIcon />
          <AlertDescription>{dispatchWarning}</AlertDescription>
        </Alert>
      )}

      <Grid
        w="100%"
        templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) minmax(360px, 400px)' }}
        templateAreas={{
          base: `"form" "rail"`,
          lg: `"form rail"`,
        }}
        columnGap={{ base: 0, lg: 5 }}
        rowGap={{ base: 4, lg: 0 }}
        alignItems="start"
      >
        <Box gridArea="form" minW={0} w="100%" pt={0}>
      <VStack spacing={2.5} align="stretch" pt={0}>
        <FormPanel title="Request" variant="mockup">
        {!isEditMode ? (
          <FormControl isInvalid={!!errors.requestId}>
            <Box position="relative">
              <Input
                ref={requestSearchInputRef}
                size="sm"
                placeholder="Search request..."
                value={requestSearchTerm}
                onChange={handleRequestSearchChange}
                onFocus={() => {
                  if (requestSearchTerm.length >= 3 && requestSearchResults.length === 0) {
                    searchRequests(requestSearchTerm)
                  }
                }}
                autoComplete="off"
                {...inputBaseProps}
              />
              {isRequestSearching && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={1}
                  p={2}
                  zIndex={1000}
                  boxShadow="md"
                >
                  <HStack justify="center">
                    <Spinner size="sm" />
                    <Text fontSize="sm">Searching...</Text>
                  </HStack>
                </Box>
              )}
              {requestSearchResults.length > 0 && !isRequestSearching && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={1}
                  maxH="300px"
                  overflowY="auto"
                  zIndex={1000}
                  boxShadow="md"
                >
                  {requestSearchResults.map((request) => (
                    <Box
                      key={request.id}
                      p={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleRequestSelect(request)
                      }}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      _last={{ borderBottom: 'none' }}
                    >
                      <Text fontSize="sm" fontWeight="500">
                        {request.id} - {request.clientName}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {request.clientLabel || request.clientOrg}
                        {request.contactNumber && ` | ${request.contactNumber}`}
                        {request.siteAddress && ` | ${request.siteAddress}`}
                        {request.serialNumber && ` | SN: ${request.serialNumber}`}
                        {request.unitModel && ` | ${request.unitModel}`}
                      </Text>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Button
              mt={1}
              size="xs"
              variant="link"
              colorScheme="blue"
              leftIcon={selectedRequestOption ? <Pencil size={14} /> : <Plus size={14} />}
              onClick={onAddRequestModalOpen}
              fontWeight="normal"
            >
              {selectedRequestOption ? 'Edit request' : 'Add request'}
            </Button>
            <FormHelperText fontSize="xs">Search by request number, client, or contact (min. 3 characters).</FormHelperText>
            <FormErrorMessage>{errors.requestId?.[0]}</FormErrorMessage>
          </FormControl>
        ) : null}

        {selectedRequestOption ? (
          <Box ref={selectedRequestDetailsRef}>
            <RequestDetailsCollapse
              key={selectedRequestOption.id}
              request={selectedRequestOption}
              formatDate={fmtDatetimeLocal}
              defaultOpen
            />
          </Box>
        ) : isEditMode ? (
          <Text fontSize="sm" color={colors.textMuted}>No linked request.</Text>
        ) : null}
        </FormPanel>

        <FormPanel title="Client & billing" variant="mockup">
          <ClientBillingSelection
            inheritedClient={selectedRequestOption?.clientId ? { id: selectedRequestOption.clientId, name: effectiveClientName! } : undefined}
            selectedClient={selectedBillingClient}
            disabled={!selectedRequestOption || saving || !(isEditMode ? access.can_edit : access.can_create)}
            disabledReason={!selectedRequestOption ? 'Select a request first.' : !(isEditMode ? access.can_edit : access.can_create) ? 'You do not have permission to assign a client to this ticket.' : undefined}
            error={errors.clientId?.[0]}
            onChange={(client) => {
              markFormDirty()
              setPendingClient(client && selectedRequestOption ? { requestId: selectedRequestOption.id, client } : null)
              loadedPurchaseOrdersClientIdRef.current = null
              setPurchaseOrders([])
              setForm((prev) => ({ ...prev, ...clearedDispatchContextFields(), clientPurchaseOrderId: null, certificationRequired: false, brandId: null }))
              dispatchSelectionRef.current = null
              setDispatchSelection(null)
              setDispatchAdditionalSelections([])
              setDispatchWindow(undefined)
              setHasUnresolvedInvalidDispatch(false)
            }}
          />
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={2} alignItems="flex-start">
              <FormControl isInvalid={Boolean(errors.clientPurchaseOrderId)}>
                <MockupFieldLabel>Purchase order</MockupFieldLabel>
                <Select
                  size="sm"
                  placeholder={isLoadingPurchaseOrders ? 'Loading...' : 'Select purchase order'}
                  value={form.clientPurchaseOrderId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value
                    updateField('clientPurchaseOrderId', value ? Number(value) : null)
                  }}
                  isDisabled={!effectiveClientId || isLoadingPurchaseOrders || (purchaseOrders.length === 0 && !form.clientPurchaseOrderId)}
                  {...inputBaseProps}
                >
                  {purchaseOrders.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.po_number}
                      {po.po_date ? ` (${formatPoDate(po.po_date)})` : ''}
                      {po.description ? ` — ${po.description}` : ''}
                    </option>
                  ))}
                </Select>
                <FormHelperText fontSize="xs">
                  {!effectiveClientId ? 'Select a client to view purchase orders.' :
                    !isLoadingPurchaseOrders && !purchaseOrderLoadError && purchaseOrders.length === 0 ? 'No purchase orders found for this client.' :
                    'Select a purchase order when applicable for billing and costing.'}
                </FormHelperText>
                <FormErrorMessage>{errors.clientPurchaseOrderId?.[0]}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <MockupFieldLabel>Billing</MockupFieldLabel>
                <Select
                  size="sm"
                  value={billableSelectValue}
                  onChange={(e) => {
                    const v = e.target.value
                    updateField('billable', v === '' ? null : v === 'true')
                  }}
                  placeholder={billingTypeLoading ? 'Loading...' : 'Not yet determined'}
                  isDisabled={billingTypeLoading}
                  {...inputBaseProps}
                >
                  {billingTypeOptions.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Text fontSize="xs" color={colors.textMuted} mt={2}>
              Billing is optional — leave unset if not yet determined.
            </Text>
          {purchaseOrderLoadError ? (
            <Box mt={2}>
              <Text role="alert" fontSize="xs" color="red.500">{purchaseOrderLoadError}</Text>
              <Button size="xs" variant="link" onClick={() => {
                loadedPurchaseOrdersClientIdRef.current = null
                setPurchaseOrderRefresh((value) => value + 1)
              }}>Retry loading purchase orders</Button>
            </Box>
          ) : null}
        </FormPanel>

        <FormPanel title="Ticket details" variant="mockup">
          <FormControl isInvalid={!!errors.subject} isRequired mb={2}>
            <Input
              ref={subjectRef}
              size="sm"
              value={form.subject}
              onChange={(e) => updateField('subject', e.target.value)}
              placeholder="Subject"
              {...inputBaseProps}
            />
            <FormHelperText fontSize="xs">
              Auto-filled from the linked request. Edit if needed.
            </FormHelperText>
            <FormErrorMessage>{errors.subject?.[0]}</FormErrorMessage>
          </FormControl>

          <Grid templateColumns="1fr" gap={2} mb={2}>
            <FormControl isInvalid={!!errors.source} isRequired>
              <MockupFieldLabel>Source</MockupFieldLabel>
              <Select
                size="sm"
                value={form.source ?? ''}
                onChange={(e) => {
                  setPrefilledFields((prev) => ({ ...prev, source: false }))
                  updateField('source', e.target.value)
                }}
                placeholder={sourceLoading ? 'Loading...' : 'Select source'}
                isDisabled={sourceLoading}
                {...inputBaseProps}
              >
                {sourceSelectOptions.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </Select>
              <FormHelperText fontSize="xs">How was this ticket initiated?</FormHelperText>
              <FormErrorMessage>{errors.source?.[0]}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.severity} isRequired>
              <MockupFieldLabel>Severity</MockupFieldLabel>
              <Select
                ref={severityRef}
                size="sm"
                value={severitySelectValue}
                onChange={(e) => {
                  setPrefilledFields((prev) => ({ ...prev, severity: false }))
                  updateField('severity', e.target.value)
                }}
                placeholder={severityLoading ? 'Loading...' : 'Select severity'}
                isDisabled={severityLoading}
                {...inputBaseProps}
              >
                {severitySelectOptions.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </Select>
              <FormHelperText fontSize="xs">
                1 = Critical (highest) · 3 = Medium (default) · 5 = Lowest. Defaults to 3 when not specified by the client.
              </FormHelperText>
              {import.meta.env.DEV && !severityLoading && severityOptions.length === 0 ? (
                <FormHelperText color="orange.500" fontSize="xs">
                  No severity options found. Run: php artisan db:seed --class=TicketOptionListSeeder
                </FormHelperText>
              ) : null}
              <FormErrorMessage>{(errors.severity ?? errors.priority)?.[0]}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.supportType}>
              <MockupFieldLabel>Support Tier</MockupFieldLabel>
              <Select
                size="sm"
                value={form.supportType ?? ''}
                onChange={(e) => updateField('supportType', e.target.value)}
                placeholder={supportTypeLoading ? 'Loading...' : 'Select support tier'}
                isDisabled={supportTypeLoading}
                {...inputBaseProps}
              >
                {supportTypeSelectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <FormHelperText fontSize="xs">
                Used for routing. Defaults from service type when not set.
              </FormHelperText>
              <FormErrorMessage>{errors.supportType?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          <Grid templateColumns="1fr" gap={2} mb={2}>
            <FormControl isInvalid={!!errors.clientReferenceNumber}>
              <MockupFieldLabel>Client Reference Number</MockupFieldLabel>
              <Input
                size="sm"
                value={form.clientReferenceNumber ?? ''}
                onChange={(e) => updateField('clientReferenceNumber', e.target.value)}
                placeholder="Client reference number"
                {...inputBaseProps}
              />
              <FormHelperText fontSize="xs">
                Optional. Use for third-party ticket references — e.g. SNow, iServe, MagiCube ticket numbers.
              </FormHelperText>
              <FormErrorMessage>{errors.clientReferenceNumber?.[0]}</FormErrorMessage>
            </FormControl>
            <Box>
              <MockupFieldLabel>Team Assignment</MockupFieldLabel>
              <TeamSearchSelect
                hideLabel
                showSelectedLine={false}
                placeholder="Group / resolver group"
                helperText="The team responsible for resolving this ticket."
                value={form.resolverTeamId ?? form.assignedTeamId}
                onChange={(teamId) => {
                  updateField('resolverTeamId', teamId)
                  updateField('assignedTeamId', teamId)
                }}
                inputBaseProps={inputBaseProps}
              />
            </Box>
          </Grid>

          <Box
            borderRadius="md"
            borderWidth={prefilledFields.issueDescription ? '1px' : 0}
            borderColor={prefilledFields.issueDescription ? 'blue.300' : undefined}
            bg={prefilledFields.issueDescription ? 'blue.50' : 'transparent'}
            p={prefilledFields.issueDescription ? 2 : 0}
          >
            <FormControl isInvalid={!!errors.issueDescription} isRequired>
              <Textarea
                ref={issueDescriptionRef}
                size="sm"
                value={form.issueDescription}
                onChange={(e) => {
                  setPrefilledFields((prev) => ({ ...prev, issueDescription: false }))
                  updateField('issueDescription', e.target.value)
                }}
                placeholder="Issue description"
                minH="50px"
                {...inputBaseProps}
              />
              {prefilledFields.issueDescription ? (
                <Badge mt={2} colorScheme="blue" fontSize="2xs">
                  From request
                </Badge>
              ) : null}
              <FormErrorMessage>{errors.issueDescription?.[0]}</FormErrorMessage>
            </FormControl>
          </Box>
        </FormPanel>

        <FormPanel title="Issue classification" variant="mockup">
        <ParentTicketCategoryCascade
          issueCategoryId={form.issueCategoryId}
          issueSubCategoryId={form.issueSubCategoryId}
          issueItemId={form.issueItemId}
          onChange={(values) => {
            setForm((prev) => ({ ...prev, ...values }))
          }}
          inputBaseProps={inputBaseProps}
        />
        </FormPanel>

        <FormPanel title="Location" variant="mockup">
          <Grid templateColumns="1fr" gap={4}>
            <FormControl>
              <MockupFieldLabel>Region</MockupFieldLabel>
              <Select
                size="sm"
                placeholder={geographicLoading ? 'Loading...' : 'Select region'}
                value={form.regionId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, regionId: e.target.value ? Number(e.target.value) : undefined, areaClusterId: undefined, provinceId: undefined, townId: undefined }))}
                isDisabled={geographicLoading}
                {...inputBaseProps}
              >
                {regionOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <MockupFieldLabel>Area Cluster</MockupFieldLabel>
              <Select
                size="sm"
                placeholder={geographicLoading ? 'Loading...' : 'Select area cluster'}
                value={form.areaClusterId ?? ''}
                onChange={(e) => {
                  const areaClusterId = e.target.value ? Number(e.target.value) : undefined
                  setForm((prev) => ({
                    ...prev,
                    areaClusterId,
                    provinceId: undefined,
                    townId: undefined,
                  }))
                }}
                isDisabled={geographicLoading || !form.regionId}
                {...inputBaseProps}
              >
                {areaClusterOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <MockupFieldLabel>Province</MockupFieldLabel>
              <Select
                size="sm"
                placeholder={
                  provinceLoading
                    ? 'Loading...'
                    : form.areaClusterId
                      ? 'Select province'
                      : 'Select area cluster first'
                }
                value={form.provinceId ?? ''}
                onChange={(e) => {
                  const provinceId = e.target.value ? Number(e.target.value) : undefined
                  setForm((prev) => ({
                    ...prev,
                    provinceId,
                    townId: undefined,
                  }))
                }}
                isDisabled={!form.areaClusterId || provinceLoading}
                {...inputBaseProps}
              >
                {provinceOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <MockupFieldLabel>Town</MockupFieldLabel>
              <Select
                size="sm"
                placeholder={
                  townLoading
                    ? 'Loading...'
                    : form.provinceId
                      ? 'Select town'
                      : 'Select province first'
                }
                value={form.townId ?? ''}
                onChange={(e) =>
                  updateField('townId', e.target.value ? Number(e.target.value) : undefined)
                }
                isDisabled={!form.provinceId || townLoading}
                {...inputBaseProps}
              >
                {townOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </FormPanel>

        <Box ref={dispatchPanelRef}>
        <FormPanel variant="mockup">
          <HStack justify="space-between" align="flex-start" mb={requiresDispatch ? 0 : undefined}>
            <Checkbox
              isChecked={requiresDispatch}
              onChange={(e) => {
                void handleDispatchCheckboxChange(e.target.checked)
              }}
              colorScheme="blue"
              size="sm"
            >
              <Text as="span" fontSize="sm">Requires field engineer dispatch</Text>
            </Checkbox>
            {isEditMode && parentTicketId && requiresDispatch ? (
              <TaskBadge
                sourceField="fe_visit"
                tasks={ticketTasks}
                onOpenTask={handleOpenFieldTask}
                allowCreate={false}
              />
            ) : null}
          </HStack>
          {requiresDispatch && selectedRequestOption && effectiveClientCode ? (
            <Box mt={3}>
              <Box mb={3} p={3} border="1px solid" borderColor={colors.borderLight} borderRadius="md">
                {coordinationTask ? (
                  <Box mb={3} pb={3} borderBottom="1px solid" borderColor={colors.borderLight}>
                    <HStack justify="space-between" flexWrap="wrap" mb={2}>
                      <Box>
                        <Text fontSize="sm" fontWeight="700">Onsite Coordination</Text>
                        <Text fontSize="xs" color={colors.textMuted}>{coordinationTask.task_number ?? `Task #${coordinationTask.id}`}</Text>
                      </Box>
                      <Badge colorScheme={coordinationTask.status === 'onsite_coordination_on_hold' ? 'orange' : coordinationTask.status === 'onsite_coordination_completed' ? 'green' : 'blue'}>
                        {coordinationTask.status_label ?? coordinationTask.status}
                      </Badge>
                    </HStack>
                    {coordinationTask.coordination_metrics ? (
                      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2}>
                        <Box><Text fontSize="xs" color={colors.textMuted}>Raw time</Text><Text fontSize="sm" fontWeight="600">{coordinationTask.coordination_metrics.raw_minutes} min</Text></Box>
                        <Box><Text fontSize="xs" color={colors.textMuted}>Paused time</Text><Text fontSize="sm" fontWeight="600">{coordinationTask.coordination_metrics.paused_minutes} min</Text></Box>
                        <Box><Text fontSize="xs" color={colors.textMuted}>Adjusted time</Text><Text fontSize="sm" fontWeight="600">{coordinationTask.coordination_metrics.adjusted_minutes} min</Text></Box>
                      </SimpleGrid>
                    ) : null}
                  </Box>
                ) : null}
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2}>Dispatch readiness</Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={0} mb={dispatchReadiness === 'on_hold' ? 3 : 0}>
                  {([
                    ['pending', 'Pending'],
                    ['on_hold', 'On Hold'],
                    ['ready', 'Ready'],
                  ] as const).map(([value, label]) => (
                    <Button
                      key={value}
                      size="sm"
                      borderRadius="0"
                      colorScheme={value === 'on_hold' ? 'orange' : 'blue'}
                      variant={dispatchReadiness === value ? 'solid' : 'outline'}
                      onClick={() => { setDispatchReadiness(value); markFormDirty() }}
                      isDisabled={isDispatchReadOnly}
                    >
                      {label}
                    </Button>
                  ))}
                </SimpleGrid>
                {dispatchReadiness === 'on_hold' ? (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} bg="orange.50" p={3} borderRadius="md">
                    <FormControl isRequired>
                      <MockupFieldLabel>Hold reason</MockupFieldLabel>
                      <Select size="sm" value={dispatchHoldReason} onChange={(e) => { setDispatchHoldReason(e.target.value); markFormDirty() }}>
                        <option value="">Select reason</option>
                        {onsiteHoldReasonOptions.map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl isRequired>
                      <MockupFieldLabel>Expected follow-up</MockupFieldLabel>
                      <Input type="datetime-local" size="sm" value={dispatchExpectedFollowUp} onChange={(e) => { setDispatchExpectedFollowUp(e.target.value); markFormDirty() }} />
                    </FormControl>
                    <FormControl gridColumn={{ md: '1 / -1' }}>
                      <MockupFieldLabel>Remarks</MockupFieldLabel>
                      <Textarea size="sm" value={dispatchHoldRemarks} onChange={(e) => { setDispatchHoldRemarks(e.target.value); markFormDirty() }} />
                    </FormControl>
                    <Text fontSize="xs" color="orange.700" gridColumn={{ md: '1 / -1' }}>
                      Dispatch preparation is paused. The parent ticket moves to On Hold (Client / Store); Ageing pauses until coordination resumes. No FE Visit will be created.
                    </Text>
                  </SimpleGrid>
                ) : dispatchReadiness === 'pending' ? (
                  <Text mt={2} fontSize="xs" color={colors.textMuted}>The parent ticket can be submitted now. No engineer schedule or FE Visit will be created.</Text>
                ) : (
                  <Text mt={2} fontSize="xs" color="green.700">Schedule and Primary FE are required. Submission will create the Parent Ticket and FE Visit together.</Text>
                )}
              </Box>
              <Box opacity={dispatchReadiness === 'ready' ? 1 : 0.5} pointerEvents={dispatchReadiness === 'ready' ? 'auto' : 'none'}>
              {isDispatchReadOnly ? (
                <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>
                    This parent ticket is closed or otherwise terminal, so field engineer dispatch cannot create new visit tasks.
                  </AlertDescription>
                </Alert>
              ) : null}
              <FEDispatchFlow
                key={`dispatch-${selectedRequestOption.id}-${effectiveClientId}`}
                mode="deferred"
                isReadOnly={isDispatchReadOnly}
                clientCode={effectiveClientCode}
                pendingClientId={selectedBillingClient?.id}
                locationValidationErrors={dispatchFieldErrors}
                locationFilter={dispatchLocationFilter}
                ticketId={isEditMode ? parentTicketId : undefined}
                initialStoreId={selectedBillingClient ? undefined : editInitialStoreId}
                initialStore={selectedBillingClient ? undefined : editInitialStore}
                initialLocation={editInitialLocation}
                seedLocationAddress={seedLocationAddress || undefined}
                primaryEngineer={(editTicket as ParentTicketWithMeta | undefined)?.primaryEngineer}
                additionalEngineers={
                  (
                    editTicket as ParentTicketWithMeta & {
                      additionalEngineers?: import('@/store/parentTicket').FieldEngineerSummary[] | null
                    }
                  )?.additionalEngineers ?? (editTicket as { backupEngineers?: import("@/store/parentTicket").FieldEngineerSummary[] | null })?.backupEngineers
                }
                hasExistingAssignment={
                  isEditMode &&
                  Boolean(
                    parentTicketId &&
                      (hasPersistedDispatch || dispatchSelection || dispatchAdditionalSelections.length > 0),
                  )
                }
                requestContactNumber={selectedRequestOption.contactNumber}
                preferredScheduleAt={selectedRequestOption.preferredScheduleAt}
                certificationRequired={Boolean(form.certificationRequired)}
                brandId={form.brandId ?? null}
                onCertificationChange={({ certificationRequired, brandId }) => {
                  markFormDirty()
                  setForm((prev) => ({
                    ...prev,
                    certificationRequired,
                    brandId,
                  }))
                }}
                confirmedSelection={dispatchSelection}
                confirmedAdditionalSelections={dispatchAdditionalSelections}
                onConfirm={handleDispatchConfirm}
                stageChanges
                initialPlanningWindow={editTicket?.dispatchPlan?.window}
                supportDurationOptions={onsiteSupportDurationOptions}
                onPlanningWindowChange={(window) => {
                  markFormDirty()
                  setDispatchWindow(window)
                }}
                onPlanningReasonChange={setDispatchChangeReason}
                onClear={() => {
                  markFormDirty()
                  dispatchSelectionRef.current = null
                  setDispatchSelection(null)
                }}
                onClearAdditional={(slotIndex?: number) => {
                  markFormDirty()
                  if (slotIndex == null) {
                    setDispatchAdditionalSelections([])
                  } else {
                    setDispatchAdditionalSelections((prev) =>
                      prev
                        .filter((_, i) => i !== slotIndex)
                        .map((backup, index) => ({
                          ...backup,
                          role: 'additional' as const,
                          additionalSlot: index + 1,
                        })),
                    )
                  }
                }}
                onStoreClear={handleDispatchStoreClear}
                onStoreSelected={handleDispatchStoreSelected}
                onLocationSelected={handleDispatchLocationSelected}
                feSchedules={(editTicket as ParentTicketWithMeta | undefined)?.feSchedules}
                existingPrimaryScheduleId={existingPrimaryScheduleId}
                existingAdditionalScheduleIds={existingAdditionalScheduleIds}
                onInvalidAssignmentChange={setHasUnresolvedInvalidDispatch}
                onSitePinConfirmedChange={setSitePinConfirmed}
              />
              </Box>
            </Box>
          ) : requiresDispatch ? (
            <Text fontSize="sm" color={colors.textMuted} mt={3}>
              Select a client above to configure field engineer dispatch.
            </Text>
          ) : null}
        </FormPanel>
        </Box>

        <FormPanel variant="mockup">
          <HStack justify="space-between" align="flex-start">
            <FormControl flex={1}>
              <Checkbox
                isChecked={Boolean(form.hasPartsNeeded)}
                onChange={(e) => setHasPartsNeeded(e.target.checked)}
                colorScheme="blue"
                size="sm"
              >
                <Text as="span" fontSize="sm">Tools / parts needed for this job</Text>
              </Checkbox>
              <FormHelperText fontSize="xs" mt={1}>
                Check if any tools, equipment, or parts are required for field work.
                {formatSourceFieldCounter('tools_parts', procurementTaskCount) ? (
                  <> · {formatSourceFieldCounter('tools_parts', procurementTaskCount)}</>
                ) : null}
              </FormHelperText>
            </FormControl>
            {isEditMode && parentTicketId && form.hasPartsNeeded ? (
              <TaskBadge
                sourceField="tools_parts"
                tasks={ticketTasks}
                onCreate={handleCreateFieldTask}
                onOpenTask={handleOpenFieldTask}
              />
            ) : null}
          </HStack>
          {form.hasPartsNeeded ? (
            <Box mt={3} p={3} border="0.5px solid" borderColor={colors.border} borderRadius="lg">
              <Text fontSize="xs" color={colors.textSecondary} mb={3}>
                Qty is how many you need (a number). Unit is how items are counted — e.g. pcs, sets, box. Example: 2 pcs.
              </Text>
              {(form.parts ?? []).map((part, idx) => (
                <HStack key={part.id ?? `part-${idx}`} mb={2} align="flex-start" spacing={2}>
                  <FormControl isRequired flex={3} isInvalid={duplicatePartIndexes.has(idx)}>
                    <MockupFieldLabel>Tool / part name</MockupFieldLabel>
                    <ToolPartNameInput
                      value={part.part_name}
                      catalogId={part.catalogId}
                      onChange={(partName, catalogId) => {
                        updatePartRow(idx, 'part_name', partName)
                        updatePartRow(idx, 'catalogId', catalogId)
                      }}
                      inputBaseProps={inputBaseProps}
                    />
                    {duplicatePartIndexes.has(idx) ? (
                      <FormErrorMessage fontSize="xs">
                        Duplicate item — each tool/part can only be listed once.
                      </FormErrorMessage>
                    ) : null}
                  </FormControl>
                  <FormControl flex={1}>
                    <MockupFieldLabel>Qty</MockupFieldLabel>
                    <Input
                      size="sm"
                      placeholder="2"
                      type="number"
                      min={0}
                      value={part.quantity}
                      onChange={(e) => updatePartRow(idx, 'quantity', e.target.value)}
                      {...inputBaseProps}
                    />
                  </FormControl>
                  <FormControl flex={1}>
                    <MockupFieldLabel>Unit</MockupFieldLabel>
                    <Input
                      size="sm"
                      placeholder="pcs, sets, box"
                      value={part.unit ?? ''}
                      onChange={(e) => updatePartRow(idx, 'unit', e.target.value)}
                      {...inputBaseProps}
                    />
                  </FormControl>
                  <Button
                    mt={6}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Remove part"
                    onClick={() => removePartRow(idx)}
                  >
                    <Trash2 size={14} />
                  </Button>
                  {part.addedAsTask ? (
                    <Badge mt={7} colorScheme="blue" variant="subtle" textTransform="none">
                      linked
                    </Badge>
                  ) : null}
                </HStack>
              ))}
              <Button size="sm" variant="outline" leftIcon={<Plus size={14} />} onClick={addPartRow}>
                Add part
              </Button>
            </Box>
          ) : null}
        </FormPanel>

        <FormPanel variant="mockup">
          <HStack justify="space-between" align="flex-start">
            <FormControl flex={1}>
              <Checkbox
                isChecked={Boolean(form.hasAttachmentsNeeded)}
                onChange={(e) => setHasAttachmentsNeeded(e.target.checked)}
                colorScheme="blue"
                size="sm"
              >
                <Text as="span" fontSize="sm">Attachments</Text>
              </Checkbox>
              <FormHelperText fontSize="xs" mt={1}>
                Check if supporting documents should be attached to this ticket.
                {isEditMode && parentTicketId
                  ? ' Use Create task to start a document collection task when the customer must submit or validate documents.'
                  : null}
                {formatSourceFieldCounter('attachments', documentTaskCount) ? (
                  <> · {formatSourceFieldCounter('attachments', documentTaskCount)}</>
                ) : null}
              </FormHelperText>
            </FormControl>
            {isEditMode && parentTicketId && form.hasAttachmentsNeeded ? (
              <TaskBadge
                sourceField="attachments"
                tasks={ticketTasks}
                onCreate={handleCreateFieldTask}
                onOpenTask={handleOpenFieldTask}
              />
            ) : null}
          </HStack>
          {form.hasAttachmentsNeeded ? (
            <ParentTicketAttachmentSection
              ticketId={parentTicketId ?? form.ticketId}
              rows={form.attachmentRows ?? []}
              onAddFiles={addAttachmentFiles}
              onRemoveRow={removeAttachmentRow}
            />
          ) : null}
        </FormPanel>

        <FormPanel title="Agent" variant="mockup">
          <FormControl isInvalid={!!errors.agentId} isRequired>
            <Box position="relative">
              <Input
                ref={agentSearchInputRef}
                size="sm"
                value={agentSearchTerm}
                onChange={handleAgentSearchChange}
                onFocus={() => {
                  if (!canAssignAgent) return
                  if (agentSearchTerm.length >= 2 && agentSearchResults.length === 0) {
                    searchAgents(agentSearchTerm)
                  }
                }}
                placeholder={
                  canAssignAgent
                    ? 'Search by name or email (min 2 characters)...'
                    : 'Assigned agent'
                }
                autoComplete="off"
                isReadOnly={!canAssignAgent}
                isDisabled={!canAssignAgent}
                {...inputBaseProps}
              />
              {canAssignAgent && isAgentSearching && (
                <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
                  <Spinner size="sm" color={colors.primary} />
                </Box>
              )}
              {canAssignAgent && agentSearchResults.length > 0 && !isAgentSearching && (
                <Box
                  position="absolute"
                  zIndex={10}
                  w="100%"
                  mt={1}
                  bg="white"
                  border="1px solid"
                  borderColor={colors.borderLight}
                  borderRadius="md"
                  boxShadow="md"
                  maxH="240px"
                  overflowY="auto"
                >
                  {agentSearchResults.map((agent) => (
                    <Box
                      key={agent.id}
                      px={4}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => handleAgentSelect(agent)}
                      borderBottom="1px solid"
                      borderColor={colors.borderLight}
                      _last={{ borderBottom: 'none' }}
                    >
                      <Text fontWeight="600" fontSize="sm" color={colors.text}>
                        {agentLabel(agent)}
                      </Text>
                      {agent.user_email && (
                        <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                          {agent.user_email}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            {currentUser && canAssignAgent && (
              <Checkbox
                mt={2}
                isChecked={assignToMe}
                onChange={handleAssignToMeChange}
                colorScheme="blue"
                size="sm"
              >
                <Text as="span" fontSize="12px">Assign to me</Text>
              </Checkbox>
            )}
            <FormErrorMessage>{errors.agentId?.[0]}</FormErrorMessage>
            <FormHelperText fontSize="xs">
              {canAssignAgent
                ? 'The agent who will own this ticket.'
                : isEditMode
                  ? 'Only a Team Lead, Senior Team Lead, Web Admin, or Developer Account can reassign the agent.'
                  : 'You are assigned as the agent. Only a Team Lead, Senior Team Lead, Web Admin, or Developer Account can reassign.'}
            </FormHelperText>
          </FormControl>
        </FormPanel>

        <FormPanel title="Notes" variant="mockup">
          <FormControl>
            <Textarea
              size="sm"
              value={form.notes ?? ''}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Internal notes..."
              rows={4}
              {...inputBaseProps}
            />
            <FormHelperText fontSize="xs">Internal notes — not visible to the client.</FormHelperText>
          </FormControl>
        </FormPanel>

        <HStack justify="flex-end" spacing={2} flexWrap="wrap">
          <Button
            variant="outline"
            onClick={() =>
              requestNavigation(() => {
                if (onCancel) onCancel()
                else navigate(-1)
              })
            }
          >
            Cancel
          </Button>
          <Button
            leftIcon={<Save size={16} />}
            variant="outline"
            colorScheme="blue"
            onClick={() => handleSubmit(false)}
            isLoading={saving && savingMode === 'draft'}
            loadingText="Saving..."
            isDisabled={
              saving ||
              (isSubmittedTicket && !readyDispatchSubmitAllowed)
            }
          >
            Save as draft
          </Button>
          <Button
            leftIcon={<Send size={16} />}
            colorScheme="blue"
            onClick={() => handleSubmit(true)}
            isLoading={saving && savingMode === 'submit'}
            loadingText={isEditMode ? 'Submitting...' : 'Creating...'}
            isDisabled={saving || !readyDispatchSubmitAllowed}
            title={
              !readyDispatchSubmitAllowed && requiresDispatch && dispatchReadiness === 'ready'
                ? 'Confirm site pin, assign a primary FE, and resolve invalid assignments before submitting'
                : undefined
            }
          >
            {requiresDispatch && dispatchReadiness === 'ready'
              ? (isSubmittedTicket ? 'Create FE Visit' : 'Submit Ticket & Create FE Visit')
              : dispatchReadiness === 'on_hold' && requiresDispatch
                ? 'Submit Ticket & Place on Hold'
                : 'Submit ticket'}
          </Button>
        </HStack>
      </VStack>
        </Box>

        <Box
          ref={railStickyRef}
          gridArea="rail"
          minW={0}
          w="100%"
          alignSelf={{ base: 'stretch', lg: 'start' }}
          position={{ base: 'relative', lg: 'sticky' }}
          top={{ lg: 0 }}
          zIndex={1}
          maxH={railMaxHeight != null ? `${railMaxHeight}px` : undefined}
          minH={0}
        >
          <Box
            ref={railScrollRef}
            w="100%"
            maxH={railMaxHeight != null ? { lg: `${railMaxHeight}px` } : undefined}
            overflowY={{ base: 'visible', lg: 'auto' }}
            overflowX="hidden"
            sx={{
              overscrollBehavior: { lg: 'contain' },
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                background: colors.borderLight ?? 'gray.300',
                borderRadius: '3px',
              },
            }}
          >
            <ParentTicketFormContextRail
              scrollContainerRef={railScrollRef}
              form={form}
              isEditMode={isEditMode}
              selectedRequest={selectedRequestOption}
              agentLabel={contextAgentLabel}
              assignToMe={assignToMe}
              requiresDispatch={requiresDispatch}
              dispatchReadiness={dispatchReadiness}
              sitePinConfirmed={sitePinConfirmed}
              dispatchSelection={dispatchSelection}
              dispatchAdditionalSelections={dispatchAdditionalSelections}
              purchaseOrder={selectedPurchaseOrder}
              clientName={effectiveClientName}
              formatPoDate={formatPoDate}
              ticketDetails={ticketDetailsPreview}
              location={locationPreview}
            />
          </Box>
        </Box>
      </Grid>

      <CreateTicketRequestModal
        isOpen={isAddRequestModalOpen}
        onClose={onAddRequestModalClose}
        mode={selectedRequestOption ? 'edit' : 'create'}
        initialRequest={selectedRequestOption}
        onCreated={(request) => {
          handleRequestSelect(request)
          onAddRequestModalClose()
        }}
        onUpdated={(request) => {
          handleRequestSelect(request)
          onAddRequestModalClose()
        }}
      />

      <Modal isOpen={confirmUncheckOpen} onClose={closeConfirmUncheck} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remove tools / parts?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              Some parts on this ticket are already linked to an active procurement task. Unchecking this box will remove
              them from the ticket <strong>and break their link to that task</strong>.
            </Text>
            <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
              <Text fontSize="sm" fontWeight="600" mb={2}>
                Linked parts affected:
              </Text>
              <VStack align="stretch" spacing={1}>
                {linkedPartsForConfirm.map((part) => (
                  <Text key={`${part.part_name}-${part.child_ticket_id ?? 'na'}`} fontSize="sm">
                    • {part.part_name}
                    {part.taskNumber || part.child_ticket_id
                      ? ` — ${part.taskNumber ?? `task #${part.child_ticket_id}`}`
                      : ' — currently in an open procurement task'}
                  </Text>
                ))}
              </VStack>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={2} onClick={closeConfirmUncheck}>
              Keep parts
            </Button>
            <Button colorScheme="red" variant="outline" onClick={confirmRemoveLinkedPartsAnyway}>
              Remove anyway
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {isEditMode && parentTicketId ? (
        <TaskDetailDrawer
          isOpen={taskDrawerOpen}
          onClose={handleTaskDrawerClose}
          parentTicketId={parentTicketId}
          task={editingTask}
          createDefaults={taskCreateDefaults}
          onSaved={() => void handleTaskSaved()}
        />
      ) : null}
    </Box>
  )
}
